import React, { useMemo } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core';
import { KeyboardTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';
import { ReviewPeriodDayType } from '../../api-client/autogenerated';
import {
  getProjectTimeZoneLabel,
  getShortTimeZoneName,
  PROJECT_TIME_ZONES,
} from '../../scripts/project-timezones';
import {
  formatCutoffTime,
  getTimeZoneError,
  getWorkingDaysSelectionError,
  parseCutoffTime,
  WEEKDAY_OPTIONS,
  WorkingDaySettingsDraft,
} from '../../scripts/working-days';

type Props = {
  value: WorkingDaySettingsDraft;
  onChange: (value: WorkingDaySettingsDraft) => void;
  disabled?: boolean;
  timeZoneLabel?: string;
  /** Construction Management setup requires a project time zone; other pages leave it optional. */
  timeZoneRequired?: boolean;
};

/**
 * KeyboardTimePicker always overwrites `InputProps.endAdornment` with its own icon button (see
 * `@material-ui/pickers`' KeyboardDateInput), so the only way to show the time zone abbreviation
 * inside the field -- rather than as a separate label beside it -- is to swap in a text field that
 * re-adds it alongside that icon, the same technique already used for the clearable date fields
 * elsewhere in the app. The abbreviation can't go in the picker's own `format` string instead: the
 * library builds its editable keyboard mask by blindly replacing every letter in `format` with the
 * mask character, with no way to escape literal text, so "hh:mm A [CDT]" would corrupt typing.
 *
 * Built via a factory (rather than defined inline in the render body) so the component's identity
 * stays stable across re-renders unless the abbreviation itself changes -- an inline definition
 * would remount the input, and its focus/cursor, on every keystroke.
 */
const makeCutoffTimeTextField = (timeZoneAbbreviation: string) =>
  function CutoffTimeTextField({ InputProps, ...other }: TextFieldProps) {
    return (
      <TextField
        {...other}
        InputProps={{
          ...InputProps,
          endAdornment: (
            <>
              {timeZoneAbbreviation && (
                <Typography variant="body1" style={{ whiteSpace: 'nowrap' }}>
                  {timeZoneAbbreviation}
                </Typography>
              )}
              {InputProps?.endAdornment}
            </>
          ),
        }}
      />
    );
  };

/**
 * The working-day configuration block shared by the subscriber admin dashboard (defaults), the
 * create-project form, and Construction Management setup (per-project overrides): calendar vs
 * working days, which weekdays count, the received-after cutoff time, and the time zone those
 * rules are evaluated in.
 */
export default function WorkingDaysSettingsFields({
  value,
  onChange,
  disabled,
  timeZoneLabel = 'Project Time Zone',
  timeZoneRequired = false,
}: Props) {
  const usesWorkingDays = value.reviewPeriodDayType === ReviewPeriodDayType.Working;
  const workingDaysError = getWorkingDaysSelectionError(value);
  const timeZoneError = getTimeZoneError(value, timeZoneRequired);

  const cutoffTimeAbbreviation = value.timeZone ? getShortTimeZoneName(value.timeZone) : '';
  const CutoffTimeTextField = useMemo(() => makeCutoffTimeTextField(cutoffTimeAbbreviation), [
    cutoffTimeAbbreviation,
  ]);

  const toggleWorkingDay = (isoWeekday: number, checked: boolean) => {
    const workingDays = checked
      ? [...value.workingDays, isoWeekday].sort((a, b) => a - b)
      : value.workingDays.filter((day) => day !== isoWeekday);
    onChange({ ...value, workingDays });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body1">Review Period Working Days</Typography>
        <RadioGroup
          row
          value={value.reviewPeriodDayType}
          onChange={(event) =>
            onChange({ ...value, reviewPeriodDayType: event.target.value as ReviewPeriodDayType })
          }
        >
          <FormControlLabel
            value={ReviewPeriodDayType.Calendar}
            control={<Radio color="primary" />}
            label="Calendar days"
            disabled={disabled}
          />
          <FormControlLabel
            value={ReviewPeriodDayType.Working}
            control={<Radio color="primary" />}
            label="Custom"
            disabled={disabled}
          />
        </RadioGroup>
        <FormHelperText>
          How document and RFI review periods are counted when calculating due dates.
        </FormHelperText>
      </Grid>

      {usesWorkingDays && (
        <>
          <Grid item xs={12}>
            <Typography variant="body1">Working Days</Typography>
            <FormGroup row>
              {WEEKDAY_OPTIONS.map((weekday) => (
                <FormControlLabel
                  key={weekday.isoWeekday}
                  control={
                    <Checkbox
                      color="primary"
                      checked={value.workingDays.includes(weekday.isoWeekday)}
                      disabled={disabled}
                      onChange={(event) =>
                        toggleWorkingDay(weekday.isoWeekday, event.target.checked)
                      }
                    />
                  }
                  label={weekday.label}
                />
              ))}
            </FormGroup>
            <FormHelperText error={!!workingDaysError}>
              {workingDaysError || 'Only the checked days count toward review periods.'}
            </FormHelperText>
          </Grid>
          <Grid item sm={6} xs={12}>
            <Typography variant="body1">Document Submission Cutoff Time</Typography>
            <MuiPickersUtilsProvider utils={DayjsUtils}>
              <KeyboardTimePicker
                inputVariant="outlined"
                fullWidth
                disabled={disabled}
                value={parseCutoffTime(value.documentCutoffTime)}
                onChange={(date) =>
                  onChange({ ...value, documentCutoffTime: formatCutoffTime(date) })
                }
                format="hh:mm A"
                TextFieldComponent={CutoffTimeTextField}
                helperText="Documents received after this time count as received the following working day."
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth error={!!timeZoneError}>
              <Typography variant="body1">
                {timeZoneLabel} {timeZoneRequired && <span style={{ color: 'red' }}>*</span>}
              </Typography>
              <Select
                disabled={disabled}
                required={timeZoneRequired}
                error={!!timeZoneError}
                value={value.timeZone}
                variant="outlined"
                fullWidth
                displayEmpty
                onChange={(event) => onChange({ ...value, timeZone: event.target.value as string })}
              >
                <MenuItem value="">Select Time Zone</MenuItem>
                {PROJECT_TIME_ZONES.map((timeZone) => (
                  <MenuItem key={timeZone} value={timeZone}>
                    {getProjectTimeZoneLabel(timeZone)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error={!!timeZoneError}>
                {timeZoneError ||
                  'Working days and the cutoff time are evaluated in this time zone.'}
              </FormHelperText>
            </FormControl>
          </Grid>
        </>
      )}
    </Grid>
  );
}
